/** Bits shared by the CRM Projects screen and the partner portal's read-only Projects screen. */
import { Badge } from '../../components/common/index.jsx';
import { date } from '../../utils/formatters.js';

export const PROJECT_STATUS = {
  planning: { label: 'Planning', tone: 'sky' },
  in_progress: { label: 'In progress', tone: 'brand' },
  on_hold: { label: 'On hold', tone: 'amber' },
  completed: { label: 'Completed', tone: 'emerald' },
  cancelled: { label: 'Cancelled', tone: 'rose' },
};
export const MILESTONE_STATUS = {
  pending: { label: 'Pending', tone: 'neutral' },
  in_progress: { label: 'In progress', tone: 'brand' },
  completed: { label: 'Completed', tone: 'emerald' },
};

export const ProjectStatusBadge = ({ status }) => <Badge tone={PROJECT_STATUS[status]?.tone}>{PROJECT_STATUS[status]?.label ?? status}</Badge>;
export const MilestoneBadge = ({ status }) => <Badge tone={MILESTONE_STATUS[status]?.tone}>{MILESTONE_STATUS[status]?.label ?? status}</Badge>;

export function Progress({ pct, done, total }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div className="d-flex justify-content-between t-2xs ink-4 mb-1"><span>{done}/{total} milestones</span><span>{pct}%</span></div>
      <div className="bar"><span style={{ width: `${pct}%`, background: pct === 100 ? 'var(--emerald)' : undefined }} /></div>
    </div>
  );
}

/** A milestone is overdue when it has a due date in the past and is not completed. */
export const isOverdue = (m) => m.status !== 'completed' && m.due_date && m.due_date < new Date().toISOString().slice(0, 10);

/** The detail body — read-only facts plus a milestone list. `renderMilestoneActions` adds CRM-side controls. */
export function ProjectDetail({ project, renderMilestoneActions }) {
  const facts = [
    ['Project ID', project.project_no], ['Customer', project.customer?.name], ['Related deal', project.deal?.name],
    ['Project manager', project.project_manager?.name], ['Start date', date(project.start_date)], ['Expected completion', date(project.expected_end_date)],
    ...(project.actual_end_date ? [['Completed on', date(project.actual_end_date)]] : []),
  ];
  return (
    <div className="d-flex flex-column gap-3 t-sm">
      <div className="d-flex align-items-center gap-2"><ProjectStatusBadge status={project.status} />
        <div className="flex-grow-1"><Progress pct={project.progress_pct} done={project.milestones_done} total={project.milestones_total} /></div></div>
      {project.description && <div>{project.description}</div>}
      <div className="row g-2">
        {facts.map(([k, v]) => <div className="col-6" key={k}><div className="field-x-label">{k}</div>{v ?? '—'}</div>)}
      </div>
      <div className="eyebrow">Milestones</div>
      {project.milestones?.length ? (
        <ol className="list-unstyled mb-0 d-flex flex-column gap-2">
          {project.milestones.map((m) => (
            <li key={m.id} className="d-flex align-items-start gap-2 p-2 px-3" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
              <i className={`bi ${m.status === 'completed' ? 'bi-check-circle-fill text-success' : m.status === 'in_progress' ? 'bi-play-circle-fill text-primary' : 'bi-circle ink-4'} mt-1`} aria-hidden="true" />
              <div className="flex-grow-1 min-w-0">
                <div className="fw-600">{m.name}</div>
                {m.description && <div className="t-xs ink-3">{m.description}</div>}
                <div className={`t-2xs ${isOverdue(m) ? 'text-danger fw-650' : 'ink-4'}`}>
                  {m.status === 'completed' ? `Completed ${date(m.completed_on)}` : m.due_date ? `${isOverdue(m) ? 'Overdue · ' : 'Due '}${date(m.due_date)}` : 'No due date'}
                </div>
              </div>
              <MilestoneBadge status={m.status} />
              {renderMilestoneActions?.(m)}
            </li>
          ))}
        </ol>
      ) : <div className="t-xs text-muted">No milestones yet.</div>}
    </div>
  );
}
